/***************************************************************************************
 * FileName      : Main.tsx
 * Description   : 메인 컴포넌트 - Matterport Showcase 초기화, 장비 Sweep 이동 + 뷰포인트 프리셋, 학습 모달/패널 제어
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.09
 * Modified Date : 2026.02.20 헤더 타이틀 h1 변경, 버전 표시 추가, 순천제일대학교 텍스트 추가
 * Reference     : @mp/bundle-sdk, @mp/common
 ***************************************************************************************/

import React, { Component } from 'react';
import { Frame, GetSDK, initComponents, sdkKey } from '@mp/common';
import { MpSdk } from '@mp/bundle-sdk/sdk';

import { AppState } from '../AppState';
// 2026.02.09 : 박은정 : 타이틀 오버레이 컴포넌트 추가 (Phase 2: 실험실 둘러보기)
import { TitleOverlay } from './TitleOverlay';
// 2026.02.13 : 박은정 : 로딩 중 상태 표시 모달 컴포넌트 추가
import { LoadingModal } from './LoadingModal';
// 2026.02.09 : 박은정 : 하단 장비 선택 탭 바 컴포넌트 추가 (Phase 3)
import { EquipmentFooter } from './EquipmentFooter';
// 2026.02.09 : 박은정 : 장비 학습하기 모달 컴포넌트 추가
import { EquipmentModal } from './EquipmentModal';
// 2026.02.09 : 박은정 : 학습 우측 패널 컴포넌트 추가
import { LearningPanel } from './LearningPanel';
// 2026.02.11 : 박은정 : 학습 좌측 모니터링 패널 컴포넌트 추가 (데이터 모니터링 + 제어 장비 상태)
import { MonitoringPanel } from './MonitoringPanel';
// 2026.02.11 : 박은정 : 플레이백 패널 컴포넌트 임포트
import { PlaybackPanel } from './PlaybackPanel';
// 2026.02.12 : 박은정 : 진입 애니메이션 오버레이 컴포넌트 추가 (건너뛰기 버튼)
import { IntroAnimationOverlay } from './IntroAnimationOverlay';
// 2026.02.12 : 박은정 : 모니터링 데이터 타입 임포트 (순환 의존성 해소를 위해 types로 이동)
import { MonitoringData } from '../types/MonitoringData';
// 2026.05.13 : 박은정 : 시뮬레이션 값 변화 누적 시계열 타입
import { ChartPoint } from './PlaybackChart';
// 2026.04.24 : 박은정 : 데이터 모니터링/시뮬레이션 카드 진입 시 실측 JSON 값 오버레이용 유틸
import { buildMonitoringData } from '../utils/monitoringDataGenerator';
// 2026.02.09 : 박은정 : 비회원 학습현황 localStorage 서비스 추가
import { LearningProgressService, CardProgress } from '../services/LearningProgressService';
// 2026.02.10 : 박은정 : 장비 JSON 데이터 서비스 추가 (sweep 정보를 JSON에서 로드)
// → enum/type은 EquipmentDataService에서 re-export (DeviceCategory, EquipmentType 등)
import { EquipmentDataService } from '../services/EquipmentDataService';
// 2026.02.11 : 박은정 : 모니터 이미지 배치 서비스 추가 (07-image-quad-placement 라이브러리)
import { getQuadImagePlacementManager, QuadImagePlacementManager } from '../services/QuadImagePlacementManager';
// 2026.02.11 : 박은정 : 모니터 배치 설정 서비스 추가 (좌표, 이미지 경로 JSON에서 로드)
import { MonitorConfigService } from '../services/MonitorConfigService';
// 2026.02.12 : 박은정 : 텍스트 라벨 배치 서비스 추가 (09-text-quad-placement 기반)
import { getQuadTextPlacementManager, QuadTextPlacementManager } from '../services/QuadTextPlacementManager';
// 2026.02.12 : 박은정 : 텍스트 라벨 설정 서비스 추가 (assets/json/text/*.json 로드)
import { TextLabelConfigService } from '../services/TextLabelConfigService';
// 2026.02.12 : 박은정 : 텍스트 라벨 타입 import (computeLabelCorners, 기본값)
// 2026.04.23 : 박은정 : UV 기반 배치 유틸 추가 (uvToLabelCorners, pushCornersAlongPlaneNormal)
import {
  computeLabelCorners,
  uvToLabelCorners,
  pushCornersAlongPlaneNormal,
  DEFAULT_LABEL_WIDTH,
  DEFAULT_LABEL_HEIGHT,
  DEFAULT_LABEL_FONT_SIZE,
  DEFAULT_LABEL_TEXT_COLOR,
  DEFAULT_LABEL_BG_COLOR,
  DEFAULT_LABEL_TEXTURE,
  DEFAULT_UV_WIDTH,
  DEFAULT_UV_HEIGHT,
} from '../types/quad-text';
// 2026.02.11 : 박은정 : 뷰포인트 프리셋 서비스 추가 (장비 이동 시 카메라 방향/줌 자동 설정)
import { ViewpointPresetService } from '../services/ViewpointPresetService';
// 2026.02.12 : 박은정 : 진입 애니메이션 서비스 추가 (Sweep 경로 순회 + 뷰포인트 프리셋 + 건너뛰기)
import { IntroAnimationService } from '../services/IntroAnimationService';
// 2026.02.20 : 박은정 : 핫스팟 태그 배치/제거 서비스 추가 (구성요소 부품 클릭 시 태그 표시)
import { HotspotTagService } from '../services/HotspotTagService';
// 2026.04.23 : 박은정 : 디바이스 3D 테두리 하이라이트 서비스 (부품 클릭 시 박스 표시)
import { DeviceHighlightService } from '../services/DeviceHighlightService';
// 2026.05.14 : 박은정 : 시뮬레이션 작동 시 장비 위 3D 텍스트 박스 오버레이 서비스
import { SimulationOverlayService } from '../services/SimulationOverlayService';
// 2026.07.02 : 박은정 : 시뮬레이션 작동(RUN=ON) 장비 위 거품 효과 서비스
import { BubbleEffectService } from '../services/BubbleEffectService';
// 2026.08.31 : 박은정 : 시뮬레이션 작동 시 장비 배관을 따라 흐르는 물/공기 효과 서비스
import { FlowEffectService, FlowConfig, normalizeConfig } from '../services/FlowEffectService';
// 2026.08.31 : 박은정 : 장비별 배관 흐름 설정 (matterport_mvp1/16-water-flow-effect 에서 튜닝한 좌표/스타일)
import circulationFlows from '../../assets/json/flow/circulation.json';
// 2025.01.27 : 박은정 : 로깅 서비스 추가 (메인 화면 진입 시 로깅)
import { logMainScreenEntry } from '../services/LoggingService';
import { cameraInputType } from '@mp/common/src/sdk-components/Camera';
import { Vector3, Quaternion, Euler, Matrix4 } from 'three';

// 2026.02.10 : 박은정 : 장비별 Sweep ID를 JSON 파일에서 로드하도록 변경 (EQUIPMENT_SWEEP_MAP 하드코딩 제거)
// 2026.02.11 : 박은정 : Sweep 정보를 assets/json/matterport/*.json으로 분리 (EquipmentDataService 경유 조회)

interface Props {
  appState: AppState;
}

interface State {
  // 2026.02.13 : 박은정 : 로딩 중 상태 표시 모달 표시 상태 추가
  showLoading: boolean;
  // 2026.02.09 : 박은정 : 타이틀 오버레이 표시 상태 추가 (Phase 2)
  showTitle: boolean;
  // 2026.02.12 : 박은정 : 진입 애니메이션 재생 중 여부 (건너뛰기 버튼 표시용)
  showIntroAnimation: boolean;
  // 2026.02.09 : 박은정 : 하단 장비 탭 바 표시 상태 및 선택 탭 (Phase 3)
  showFooter: boolean;
  activeEquipment: string;
  // 2026.02.09 : 박은정 : 장비 학습하기 모달 표시 상태
  showEquipmentModal: boolean;
  modalEquipmentId: string | null;
  // 2026.02.09 : 박은정 : 장비별 학습 진행률(%) 및 카드 상태 (localStorage 연동)
  equipmentProgress: Record<string, number>;
  cardStatuses: Record<string, CardProgress>;
  // 2026.02.09 : 박은정 : 학습 우측 패널 표시 상태 및 활성 카드
  showLearningPanel: boolean;
  learningPanelEquipmentId: string | null;
  learningPanelCardId: string | null;
  // 2026.02.11 : 박은정 : 모니터링 데이터 (LearningPanel의 monitoring 카드에서 생성된 값)
  monitoringData: Record<string, MonitoringData | null>;
  // 2026.05.13 : 박은정 : 시뮬레이션 누적 시계열 (좌측 모니터링 패널 최상단 차트용)
  chartHistory: ChartPoint[];
  // 2026.02.11 : 박은정 : 플레이백 시나리오 ID
  playbackScenarioId: Record<string, string | null>;
  // 2026.02.20 : 박은정 : 현재 핫스팟 태그가 표시된 디바이스 ID
  activeDeviceId: string | null;
  // 2026.04.24 : 박은정 : 현재 활성 디바이스의 내부 채우기(fill) 표시 여부 — 3-state 토글의 2단계
  activeDeviceFillVisible: boolean;
  // 2026.05.14 : 박은정 : 모바일(좁은 화면) 감지 — 학습 기능 사이드바를 숨기고 3D 탐색만 노출
  isMobile: boolean;
}

// 2026.05.14 : 박은정 : 학습 UI 가 화면을 가리지 않을 최소 가로폭 (px). 미만이면 모바일 모드.
const MOBILE_BREAKPOINT_PX = 1024;

// 2026.07.02 : 박은정 : 거품 효과 파라미터 (matterport_mvp1/14-bubble-effect bubble-config.json 튜닝값)
const BUBBLE_EMITTER_DEFAULTS = {
  radius: 0.009,
  spawnRate: 50,
  riseSpeed: 0.35,
  lifetime: 1,
  spread: 0.14,
  color: 0xddf2ff,
};
// 2026.07.02 : 박은정 : 거품 emitter id 접두사 (bubble-{equipmentId}-{deviceId} 로 네임스페이스)
const BUBBLE_ID_PREFIX = 'bubble-';
// 2026.07.02 : 박은정 : circulation 시나리오 거품 고정 위치 (matterport_mvp1/14-bubble-effect bubble-config.json 좌/우 튜닝값)
//   RUN 디바이스 anchor 는 장비 중앙·낮은 곳에 몰려 있어, 예제에서 좌우 탱크 끝으로 튜닝한 고정 좌표를 사용.
//   PP-302=왼쪽, BF-304=오른쪽 (실제 X좌표 순서와 일치). 매핑 없는 장비/디바이스는 기존대로 anchor 로 폴백.
const BUBBLE_FIXED_POSITIONS: Record<string, Record<string, { x: number; y: number; z: number }>> = {
  circulation: {
    'PP-302': { x: -1.2569292736053468, y: 0.7099067115783688, z: -5.669666595458984 },
    'BF-304': { x: 0.7130707263946537, y: 0.6599067115783688, z: -5.779666595458982 },
  },
};

// 2026.08.31 : 박은정 : 거품 효과를 띄울 장비.
//   위 BUBBLE_FIXED_POSITIONS 에 없는 장비는 핫스팟 anchor 로 폴백하게 되어 있어,
//   RUN=ON 이기만 하면 증류탑·열교환기에도 거품이 떴다. 순환기 시뮬레이션에서만 보여야 하므로
//   장비를 명시적으로 제한한다.
const BUBBLE_ENABLED_EQUIPMENT = new Set<string>(['circulation']);

// 2026.08.31 : 박은정 : 장비별 배관 흐름 설정.
//   좌표·매질·방향·스타일은 matterport_mvp1/16-water-flow-effect 에서 실제 장면을 보며 잡은 값이라
//   여기서는 파일을 그대로 읽어 쓰기만 한다. 재튜닝이 필요하면 mvp1 에서 고쳐 JSON 만 교체한다.
//   id 는 장비명으로 네임스페이스해 다른 장비 설정과 섞이지 않게 한다.
//   정규화는 모듈 로드 시 한 번만 — 모니터링 데이터가 올 때마다 다시 만들 필요가 없다.
const namespaceFlows = (equipmentId: string, raws: unknown[]): FlowConfig[] =>
  raws.map(raw => {
    const cfg = normalizeConfig(raw as never);
    return { ...cfg, id: `${equipmentId}-${cfg.id}` };
  });

const EQUIPMENT_FLOWS: Record<string, FlowConfig[]> = {
  circulation: namespaceFlows('circulation', circulationFlows as unknown[]),
};

// 2026.08.31 : 박은정 : 흐름이 어느 시나리오에 속하는지 (네임스페이스된 id 기준).
//   순환기 배관은 좌/우가 각각 다른 장비에 붙어 있고 시나리오도 따로다.
//   좌(PP-302 최근접 0.26m) / 우(BF-304 최근접 0.66m) 로 좌표상 명확히 갈린다.
//   여기 없는 흐름은 시나리오와 무관하게 항상 표시한다.
const FLOW_SCENARIOS: Record<string, string> = {
  'circulation-water-1787902362345': 'circulation-pp302-manual', // 순환기(left)  — PP-302
  'circulation-air-1788159282675': 'circulation-bf304-manual',   // 순환기(right) — BF-304
};

export class Main extends Component<Props, State> {
  private sdk: MpSdk = null;
  // 2026.02.09 : 박은정 : 학습현황 서비스 인스턴스 (싱글톤)
  private progressService = LearningProgressService.getInstance();
  // 2026.02.10 : 박은정 : 장비 데이터 서비스 인스턴스 (JSON에서 sweep 정보 로드)
  private equipmentDataService = EquipmentDataService.getInstance();
  // 2026.02.11 : 박은정 : 모니터 이미지 배치 관리 서비스 (싱글톤)
  private quadImageManager: QuadImagePlacementManager = getQuadImagePlacementManager();
  // 2026.02.11 : 박은정 : 모니터 배치 설정 서비스 (좌표, 이미지 경로)
  private monitorConfigService = MonitorConfigService.getInstance();
  // 2026.02.12 : 박은정 : 텍스트 라벨 배치 관리 서비스 (싱글톤)
  private quadTextManager: QuadTextPlacementManager = getQuadTextPlacementManager();
  // 2026.02.12 : 박은정 : 텍스트 라벨 설정 서비스 (JSON 데이터 로드)
  private textLabelConfigService = TextLabelConfigService.getInstance();
  // 2026.02.11 : 박은정 : 뷰포인트 프리셋 서비스 (장비 이동 시 카메라 방향/줌 자동 설정)
  private viewpointPresetService = ViewpointPresetService.getInstance();
  // 2026.02.12 : 박은정 : 진입 애니메이션 서비스 (Sweep 경로 순회 + 뷰포인트 + 건너뛰기)
  private introAnimationService = IntroAnimationService.getInstance();
  // 2026.02.20 : 박은정 : 핫스팟 태그 배치/제거 서비스 (구성요소 부품 클릭 시 태그 표시)
  private hotspotTagService = HotspotTagService.getInstance();
  // 2026.04.23 : 박은정 : 디바이스 3D 테두리 하이라이트 서비스 (부품 클릭 시 박스 표시)
  private deviceHighlightService = DeviceHighlightService.getInstance();
  // 2026.05.14 : 박은정 : 시뮬레이션 작동 시 장비 위 3D 텍스트 박스 오버레이 서비스
  private simulationOverlayService = SimulationOverlayService.getInstance();
  // 2026.07.02 : 박은정 : 시뮬레이션 작동 장비 위 거품 효과 서비스
  private bubbleEffectService = BubbleEffectService.getInstance();
  // 2026.08.31 : 박은정 : 배관 흐름 효과 서비스 인스턴스 (싱글톤)
  private flowEffectService = FlowEffectService.getInstance();
  // 2026.08.31 : 박은정 : 배관이 다 차는 시각에 거품을 다시 판정하기 위한 타이머.
  //   모니터링 데이터 tick 에만 기대면 재생이 끝난 뒤 데이터가 멈춰 거품이 영영 안 뜬다.
  private flowFillTimer: ReturnType<typeof setTimeout> | null = null;
  // 2026.08.31 : 박은정 : 직전 충전 판정 (바뀔 때만 로그를 남기기 위한 값)
  private lastFlowFilled: boolean | null = null;
  private cameraInput: MpSdk.Scene.IComponent;
  private src: string;
  private applicationKey: string;
  private sceneObject: MpSdk.Scene.IObject = null;
  // 2026.04.27 : 박은정 : placeTextLabels 재진입 방지 가드
  //   - 어떤 이유로 componentDidMount가 두 번 실행돼도 라벨이 두 번 배치되지 않게 보장
  //   - true → 1회 진입 후 잠금, false → 첫 진입 허용
  private textLabelsPlaced: boolean = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      // 2026.02.13 : 박은정 : 로딩 모달 초기 상태 true (SDK 초기화 시작 시 표시)
      showLoading: true,
      // 2026.02.09 : 박은정 : 타이틀 오버레이 초기 상태 false (Showcase 로드 완료 후 표시)
      showTitle: false,
      // 2026.02.12 : 박은정 : 진입 애니메이션 초기 상태 (재생 전)
      showIntroAnimation: false,
      // 2026.02.09 : 박은정 : 하단 장비 탭 바 초기 상태 false, 기본 선택 탭 열교환기
      showFooter: false,
      activeEquipment: 'heat-exchanger',
      // 2026.02.09 : 박은정 : 장비 학습하기 모달 초기 상태
      showEquipmentModal: false,
      modalEquipmentId: null,
      // 2026.02.09 : 박은정 : 학습현황 초기값 (componentDidMount에서 localStorage 로드)
      equipmentProgress: {},
      cardStatuses: {},
      // 2026.02.09 : 박은정 : 학습 패널 초기 상태 (숨김)
      showLearningPanel: false,
      learningPanelEquipmentId: null,
      learningPanelCardId: null,
      // 2026.02.11 : 박은정 : 모니터링 데이터 초기 상태 (빈 객체)
      monitoringData: {},
      // 2026.05.13 : 박은정 : 시뮬레이션 시계열 초기 상태 (빈 배열)
      chartHistory: [],
      // 2026.02.11 : 박은정 : 플레이백 시나리오 ID 초기 상태 (빈 객체)
      playbackScenarioId: {},
      // 2026.02.20 : 박은정 : 핫스팟 태그 활성 디바이스 초기 상태 (없음)
      activeDeviceId: null,
      // 2026.04.24 : 박은정 : 면 채우기 초기 상태 false (점선 테두리만 표시)
      activeDeviceFillVisible: false,
      // 2026.05.14 : 박은정 : 초기 화면 너비로 모바일 여부 판정
      isMobile: typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX,
    };

    const params = objectFromQuery();
    params.m = params.m || 'U4AxQrk4Wfj';
    params.play = params.play || '1';
    params.qs = params.qs || '1';
    params.sr = params.sr || '-.15';
    params.applicationKey = params.applicationKey || sdkKey;

    // 2026.02.12 : 박은정 : 매터포트 Showcase 기본 UI 버튼 숨김 URL 파라미터 (Bundle SDK 보조용)
    params.help = '0';        // 도움말 버튼 숨김
    params.brand = '0';       // Matterport 브랜딩(로고/이용약관) 숨김
    // 2026.02.21 : 박은정 : mt=0은 Tag.add()로 동적 추가한 핫스팟 태그까지 숨김 → 기본 태그는 removeDefaultTags()로 제거
    // params.mt = '0';
    params.pin = '0';         // 바닥 네비게이션 핀 숨김
    this.applicationKey = params.applicationKey;

    const queryString = Object
      .keys(params)
      .map((key) => key + '=' + params[key])
      .join('&');
    this.src = `./bundle/showcase.html?${queryString}`;
  }

  // 2026.05.14 : 박은정 : 모바일 여부 갱신 (resize / orientationchange 시 호출)
  private handleResize = () => {
    const next = window.innerWidth < MOBILE_BREAKPOINT_PX;
    if (next !== this.state.isMobile) {
      this.setState({ isMobile: next });
    }
  };

  async componentDidMount() {
    // 2026.05.14 : 박은정 : 화면 크기 변화 감지 (가로/세로 모드 전환 포함)
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleResize);

    // 2025.01.27 : 박은정 : 메인 화면 진입 시 로깅
    await logMainScreenEntry('메인 화면 진입');
    
    this.sdk = await GetSDK('sdk-iframe', this.applicationKey);
    // 2026.02.21 : 박은정 : CSS 주입은 Showcase 로딩 완료(PLAYING) 후 실행 (아래 waitUntil 이후로 이동)
    await initComponents(this.sdk);
    await this.createCameraControl();
    await this.sdk.Scene.configure((renderer: any, three: any) => {
      // 2026-08-28 : renderer.physicallyCorrectLights 대입 제거.
      //   three r150+ 에서 이 속성은 삭제됐다. 대입해도 아무 효과가 없고
      //   "has been removed. Set renderer.useLegacyLights instead." 경고만 났다.
      //   useLegacyLights 로 바꾸지 않은 이유: 그건 실제로 조명 계산을 바꿔
      //   운영 화면의 밝기가 달라질 수 있다. 죽은 줄만 지워 경고를 없앤다.
      //   조명을 물리 기반으로 전환하려면 별도로 육안 검증 후 진행할 것.
      renderer.outputEncoding = three.sRGBEncoding;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.bias = 0.0001;
      renderer.shadowMap.type = three.PCFSoftShadowMap;
    });
    
    // 2026.02.09 : 박은정 : localStorage에서 학습현황 로드 → state 반영
    this.loadLearningProgress();

    // 2026.02.11 : 박은정 : 뷰포인트 프리셋 서비스 초기화 (SDK 인스턴스 전달)
    this.viewpointPresetService.initialize(this.sdk);

    // 2026.02.12 : 박은정 : 진입 애니메이션 서비스 초기화 (SDK 인스턴스 전달)
    this.introAnimationService.initialize(this.sdk);

    // 2026.02.20 : 박은정 : 핫스팟 태그 서비스 초기화 (SDK 인스턴스 전달)
    this.hotspotTagService.initialize(this.sdk);
    // 2026.04.23 : 박은정 : 하이라이트 서비스 초기화 (mp.deviceOutline 커스텀 컴포넌트 등록)
    await this.deviceHighlightService.initialize(this.sdk);
    // 2026.07.02 : 박은정 : 거품 효과 서비스 초기화 (mp.bubbleEmitter 커스텀 컴포넌트 등록)
    await this.bubbleEffectService.initialize(this.sdk);
    // 2026.08.31 : 박은정 : 배관 흐름 효과 서비스 초기화 (mp.mediaFlow 커스텀 컴포넌트 등록)
    await this.flowEffectService.initialize(this.sdk);

    // 2026.02.12 : 박은정 : 매터포트 공간에 등록된 기존 Mattertag(자동 생성 태그) 모두 제거
    await this.removeDefaultTags();

    // 2026.02.11 : 박은정 : 3대 모니터에 장비 이미지 배치 (SDK 초기화 후 즉시 실행)
    await this.placeMonitorImages();

    // 2026.02.12 : 박은정 : 전체 장비의 텍스트 라벨 배치 (SDK 초기화 후 즉시 실행)
    await this.placeTextLabels();

    // 2026.04.24 : 박은정 : 초기 진입 시 라벨을 JSON 기본 텍스트(text 필드, 예: "100%")로 그대로 표시.
    //   - 별도 알고리즘 더미 오버레이를 하지 않아 "더미 텍스트 값 이중 로드" 문제 제거.
    //   - 이후 모니터링/시뮬레이션 카드 진입 시 실측 JSON 값이 덮어써서 텍스트 변화가 명확히 드러남.
    const equipmentIdsForInit = this.textLabelConfigService.getEquipmentIdsWithLabels();
    for (const equipmentId of equipmentIdsForInit) {
      this.setTextLabelsVisible(equipmentId, true);
    }

    // 2026.02.12 : 박은정 : Showcase 로드 완료 후 시작 Sweep + 뷰포인트 즉시 적용 → 타이틀 오버레이 표시
    try {
      await this.sdk.App.state.waitUntil(
        (state: any) => state.phase === this.sdk.App.Phase.PLAYING
      );
    } catch (error) {
      // Showcase 상태 대기 실패 시에도 타이틀 오버레이 표시
    }
    // 2026.02.21 : 박은정 : Showcase 로딩 완료 후 UI 숨김 CSS 주입 (태그 오버레이는 유지, 특정 UI 요소만 숨김)
    this.hideShowcaseUI();

    // 2026.02.12 : 박은정 : 타이틀 모달 표시 전에 시작 Sweep + 뷰포인트 적용 (모달이 뜰 때 이미 해당 위치)
    await this.introAnimationService.applyStartPosition();
    // 2026.02.20 : 박은정 : 로딩 완료 후 0.5초 대기 후 로딩 모달 숨김 및 타이틀 모달 표시
    setTimeout(() => {
      this.setState({ showLoading: false, showTitle: true });
    }, 1000);
  }

  componentWillUnmount() {
    // 2026.05.14 : 박은정 : resize / orientationchange 리스너 해제
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleResize);
  }


  // 2026.02.12 : 박은정 : Showcase iframe 내부에 CSS 주입하여 기본 UI 버튼 완전 숨김
  // 2026.02.12 : 박은정 : Bundle SDK에서는 URL 파라미터가 동작하지 않으므로 CSS 직접 주입 방식 사용
  // 2026.02.12 : 박은정 : 대상 — 공유, VR, 전체화면, 도움말, 이용약관, Matterport, 평면도, 측정, 검색 버튼
  // 2026.02.21 : 박은정 : Showcase 로딩 완료 후 특정 UI 요소만 숨김 (태그 오버레이는 유지)
  // #react-render-root 전체를 숨기면 Tag.add() 핫스팟 태그도 숨겨지므로, 개별 UI 요소를 선택적으로 숨김
  private hideShowcaseUI() {
    try {
      const iframe = document.getElementById('sdk-iframe') as HTMLIFrameElement;
      if (!iframe || !iframe.contentDocument) return;

      const style = iframe.contentDocument.createElement('style');
      style.textContent = `
        /* 2026.02.21 : 박은정 : 하단 툴바 숨김 */
        #bottom-ui {
          display: none !important;
        }
        /* 2026.02.21 : 박은정 : 상단 툴바 숨김 */
        .top-ui {
          display: none !important;
        }
      `;
      iframe.contentDocument.head.appendChild(style);
    } catch (e) {
      // iframe 접근 실패 시 무시
    }
  }

  // 2026.02.12 : 박은정 : 매터포트 공간에 기본 등록된 Mattertag/Tag 모두 제거
  // 2026.02.12 : 박은정 : URL 파라미터(mt=0)로 숨겨도 잔여 태그가 있을 수 있으므로 SDK로 완전 제거
  private async removeDefaultTags() {
    try {
      // Mattertag (레거시 API) 제거 시도
      if (this.sdk.Mattertag) {
        const mattertags = await this.sdk.Mattertag.getData();
        if (mattertags && mattertags.length > 0) {
          const tagSids = mattertags.map((tag: any) => tag.sid);
          await this.sdk.Mattertag.remove(tagSids);
        }
      }
    } catch (e) {
      // Mattertag API가 없거나 실패해도 무시
    }
    try {
      // Tag (신규 API) 제거 시도 — data 구독으로 ID 수집 후 일괄 제거
      if (this.sdk.Tag) {
        const tagIds: string[] = [];
        const sub = this.sdk.Tag.data.subscribe({
          onCollectionUpdated: (collection: { [key: string]: any }) => {
            Object.keys(collection).forEach((id: string) => tagIds.push(id));
          },
        });
        // 구독 즉시 해제 후 수집된 태그 ID 제거
        if (sub && sub.cancel) sub.cancel();
        if (tagIds.length > 0) {
          await this.sdk.Tag.remove(...tagIds);
        }
      }
    } catch (e) {
      // Tag API가 없거나 실패해도 무시
    }
  }

  // 2026.02.11 : 박은정 : 3대 모니터에 장비 이미지 배치 (matterport JSON 기반)
  // 2026.02.11 : 박은정 : 초기 배치 시 visible: true → 학습 시나리오에 구애받지 않고 처음부터 끝까지 표시
  private async placeMonitorImages() {
    try {
      // 2026.02.11 : 박은정 : QuadImagePlacementManager SDK 초기화
      await this.quadImageManager.initialize(this.sdk);

      // 2026.02.11 : 박은정 : 모니터 설정 JSON에서 전체 모니터 목록 조회
      const monitors = this.monitorConfigService.getAllMonitors();

      for (const monitor of monitors) {
        await this.quadImageManager.addImage({
          id: monitor.id,
          name: monitor.name,
          imageUrl: monitor.defaultImageUrl,
          corners: monitor.corners,
          opacity: 1.0,
          visible: true, // 2026.02.11 : 박은정 : 처음부터 끝까지 항상 표시 (학습 시나리오에 구애받지 않음)
          textureSize: { w: 1024, h: 1024 },
        });
      }
    } catch (error) {
      // 에러 무시
    }
  }

  // 2026.02.12 : 박은정 : 전체 장비의 텍스트 라벨 배치 (text JSON 기반)
  // 2026.02.12 : 박은정 : 초기 배치 시 visible: false → 데이터 모니터링/시뮬레이션 카드 진입 시 표시
  private async placeTextLabels() {
    // 2026.04.27 : 박은정 : 재진입 방지 — 어떤 경로로든 두 번째 호출은 즉시 종료
    //   placeTextLabels 가 두 번 실행돼 같은 ID로 addText 가 두 번 일어나면
    //   이전 노드의 메시가 잔존하며 텍스트가 두 번 뿌려지는 현상이 발생
    if (this.textLabelsPlaced) {
      console.warn('[Main] placeTextLabels 재진입 차단', new Error('재진입 stack').stack);
      return;
    }
    this.textLabelsPlaced = true;

    try {
      // 2026.04.27 : 박은정 : 호출 횟수 추적 (이중 라벨 원인 진단용 — 정상이면 1회만 출력)
      //   호출자(스택) 함께 출력 — 두 번째 호출 발생 시 어디서 부르는지 식별
      // 2026-08-28 : 진단 완료로 운영 콘솔에서 제거. 스택까지 찍혀 오류처럼 보였다.
      //   다시 확인이 필요하면 아래 주석을 해제할 것.
      // console.log('[Main] placeTextLabels 호출', new Error('placeTextLabels stack').stack);
      // 2026.02.12 : 박은정 : QuadTextPlacementManager SDK 초기화
      await this.quadTextManager.initialize(this.sdk);

      // 2026.04.24 : 박은정 : 이전 라벨 노드가 남아있으면 전부 제거 (HMR/재진입 시 중복 배치 방지)
      //   → "더미 텍스트 이중 표시" 원인 제거
      await this.quadTextManager.removeAllTexts();

      // 2026.02.12 : 박은정 : 텍스트 라벨이 있는 전체 장비 ID 조회
      const equipmentIds = this.textLabelConfigService.getEquipmentIdsWithLabels();

      for (const equipmentId of equipmentIds) {
        const labels = this.textLabelConfigService.getTextLabels(equipmentId);

        for (const label of labels) {
          // 2026.04.23 : 박은정 : UV 기반 항목(monitor + u/v) 우선.
          //   없으면 레거시 anchorPosition + rotation 경로로 fallback.
          let corners;
          if (label.monitor !== undefined && label.u !== undefined && label.v !== undefined) {
            const monitor = this.monitorConfigService.getMonitorById(label.monitor);
            if (!monitor) {
              continue; // 매칭되는 모니터 없음 → 이 라벨 건너뜀
            }
            const baseCorners = uvToLabelCorners(
              monitor.corners,
              label.u,
              label.v,
              label.uWidth ?? DEFAULT_UV_WIDTH,
              label.uHeight ?? DEFAULT_UV_HEIGHT,
            );
            // 2026.04.23 : 박은정 : 스캔 메시에 가려지지 않도록 카메라 방향으로 5mm 이격
            corners = pushCornersAlongPlaneNormal(baseCorners, monitor.corners, 0.005);
          } else if (label.anchorPosition) {
            // 2026.02.12 : 박은정 : 앵커 위치 + 회전 + 크기 → 4개 꼭짓점 계산
            const rotation = label.rotation || { x: 0, y: 0, z: 0 };
            corners = computeLabelCorners(
              label.anchorPosition,
              { x: 0, y: 0, z: 0 },
              DEFAULT_LABEL_WIDTH,
              DEFAULT_LABEL_HEIGHT,
              rotation,
            );
          } else {
            continue; // 좌표 정보 없음
          }

          // 2026.02.12 : 박은정 : 텍스트 배치 설정 생성 (초기 visible: false)
          await this.quadTextManager.addText({
            id: label.id,
            name: label.label,
            text: label.text || label.label,
            textStyle: {
              fontSize: label.fontSize || DEFAULT_LABEL_FONT_SIZE,
              fontFamily: 'Arial, sans-serif',
              fontWeight: 'bold',
              textColor: label.textColor || DEFAULT_LABEL_TEXT_COLOR,
              backgroundColor: label.backgroundColor || DEFAULT_LABEL_BG_COLOR,
              textAlign: 'center',
              lineHeight: 1.2,
              // 2026.04.23 : 박은정 : 09 프로젝트(소스)와 동일하게 8px 로 동기화
              padding: 8,
            },
            corners,
            opacity: 1.0,
            visible: false, // 2026.02.12 : 박은정 : 초기 숨김 → 모니터링/시뮬레이션 카드 진입 시 표시
            // 2026.04.23 : 박은정 : 09 프로젝트와 동일한 DEFAULT_LABEL_TEXTURE(512×128) 사용.
            //   (같은 fontSize 에서 동일한 시각적 크기로 렌더되도록. QuadTextPlane의
            //    renderText 자동 확대 로직이 fontSize 가 클 경우 캔버스를 키워줌)
            textureSize: DEFAULT_LABEL_TEXTURE,
            showBorder: false,
          });
        }
      }
    } catch (error) {
      // 2026.02.12 : 박은정 : 텍스트 라벨 배치 실패 시 에러 무시 (앱 실행에 영향 없음)
    }
  }

  // 2026.02.12 : 박은정 : 특정 장비의 텍스트 라벨 표시/숨김 토글
  // 2026.02.12 : 박은정 : 데이터 모니터링 또는 시뮬레이션 카드 진입/이탈 시 호출
  private setTextLabelsVisible(equipmentId: string, visible: boolean) {
    if (!this.quadTextManager.isInitialized()) return;

    const labels = this.textLabelConfigService.getTextLabels(equipmentId);
    for (const label of labels) {
      this.quadTextManager.updateText(label.id, { visible });
    }
  }

  // 2026.04.24 : 박은정 : 전체 장비 텍스트 라벨을 JSON 기본 텍스트(더미)로 복원(visible=true)
  //   - 더미 텍스트 소스는 JSON text 필드 하나뿐 → "더미 값 두 개 로드" 문제 제거
  //   - monitoring/scenario 이탈 시 호출되어 이전 실측값/숨김 상태를 기본 더미로 되돌림
  private restoreAllTextLabelsToDummy() {
    if (!this.quadTextManager.isInitialized()) return;

    const equipmentIds = this.textLabelConfigService.getEquipmentIdsWithLabels();
    for (const equipmentId of equipmentIds) {
      const labels = this.textLabelConfigService.getTextLabels(equipmentId);
      for (const label of labels) {
        const defaultText = label.text || label.label;
        this.quadTextManager.updateText(label.id, { text: defaultText, visible: true });
      }
    }
  }

  // 2026.04.24 : 박은정 : 데이터 모니터링/시뮬레이션 카드 활성 장비에 실측 값 오버레이
  //   - 매칭 키 있음: 실측 값으로 텍스트 갱신 + visible=true
  //   - 매칭 키 없음: 라벨 숨김 → JSON 기본 더미가 남아 실측값과 겹쳐 보이지 않게 제거
  private applyMonitoringDataToLabels(equipmentId: string, data: MonitoringData | null) {
    if (!this.quadTextManager.isInitialized()) return;
    if (!data) return;

    const labels = this.textLabelConfigService.getTextLabels(equipmentId);
    const { sensorValues, controlValues } = data;

    for (const label of labels) {
      const value = controlValues?.[label.id] ?? sensorValues?.[label.id];
      if (value !== undefined) {
        this.quadTextManager.updateText(label.id, { text: value, visible: true });
      } else {
        this.quadTextManager.updateText(label.id, { visible: false });
      }
    }
  }

  // 2026.05.14 : 박은정 : 모니터 텍스트 라벨 전부 숨김 (구성요소 카드 진입 시 — 부품 클릭 전 빈 화면)
  private hideAllTextLabels() {
    if (!this.quadTextManager.isInitialized()) return;
    const equipmentIds = this.textLabelConfigService.getEquipmentIdsWithLabels();
    for (const eqId of equipmentIds) {
      const labels = this.textLabelConfigService.getTextLabels(eqId);
      for (const label of labels) {
        this.quadTextManager.updateText(label.id, { visible: false });
      }
    }
  }

  // 2026.05.14 : 박은정 : 모니터 라벨을 deviceId 매칭만 노출 (구성요소 카드에서 부품 클릭 시).
  //   - label.id 가 `{deviceId}-{suffix}` 형태일 때만 표시
  //   - 매칭 라벨: 데이터 값으로 갱신 + visible=true
  //   - 비매칭 라벨: 숨김 (이전 상태 잔류 방지)
  private applyMonitoringDataToLabelsForDevice(equipmentId: string, deviceId: string, data: MonitoringData | null) {
    if (!this.quadTextManager.isInitialized()) return;
    const labels = this.textLabelConfigService.getTextLabels(equipmentId);
    const sensorValues = data?.sensorValues;
    const controlValues = data?.controlValues;

    for (const label of labels) {
      const lastDash = label.id.lastIndexOf('-');
      const labelDeviceId = lastDash > 0 ? label.id.substring(0, lastDash) : label.id;
      if (labelDeviceId !== deviceId) {
        this.quadTextManager.updateText(label.id, { visible: false });
        continue;
      }
      const value = controlValues?.[label.id] ?? sensorValues?.[label.id];
      if (value !== undefined) {
        this.quadTextManager.updateText(label.id, { text: String(value), visible: true });
      } else {
        // 데이터에 없는 키는 JSON 기본 텍스트로 표시 (deviceId 자체 라벨)
        const defaultText = label.text || label.label;
        this.quadTextManager.updateText(label.id, { text: defaultText, visible: true });
      }
    }
  }

  // 2026.02.20 : 박은정 : 구성요소 부품 클릭 시 핫스팟 태그 토글 (표시/숨김)
  // 2026.02.20 : 박은정 : 같은 디바이스 재클릭 → 태그 제거, 다른 디바이스 클릭 → 이전 제거 + 새 태그 배치
  // 2026.04.15 : 박은정 : 3D 테두리 하이라이트 박스 함께 표시/제거
  // 2026.04.24 : 박은정 : 3-state 토글 — 처음 클릭: 점선+면 채우기 / 재클릭: 점선만(면 제거) / 한 번 더: 전체 해제
  private handleDeviceClick = async (equipmentId: string, deviceId: string) => {
    console.log(`[Main] handleDeviceClick 호출: equipmentId=${equipmentId}, deviceId=${deviceId}`);
    const { activeDeviceId, activeDeviceFillVisible } = this.state;

    if (activeDeviceId !== deviceId) {
      // 다른 디바이스 클릭 → 이전 태그/하이라이트 제거 후 점선 + 면 채우기 표시
      await this.hotspotTagService.removeAllTags();
      await this.deviceHighlightService.removeHighlight();
      const result = await this.hotspotTagService.addTag(equipmentId, deviceId);
      console.log(`[Main] addTag 결과: ${result}, placedCount=${this.hotspotTagService.getPlacedTagCount()}`);
      const tagData = this.hotspotTagService.getTagData(equipmentId, deviceId);
      if (tagData) {
        await this.deviceHighlightService.showHighlight({
          position: tagData.anchorPosition,
          boxSize: tagData.boxSize,
          boxOffset: tagData.boxOffset,
          fillVisible: true,
        });
      }
      this.setState({ activeDeviceId: deviceId, activeDeviceFillVisible: true });
      // 2026.05.14 : 박은정 : 구성요소 카드 활성 시 → 해당 디바이스의 3D 오버레이 + 모니터 텍스트 라벨만 표시
      if (this.state.learningPanelCardId === 'components') {
        const data = this.state.monitoringData[equipmentId] ?? buildMonitoringData(equipmentId);
        this.simulationOverlayService.ensureLabelsForDevice(equipmentId, deviceId, data);
        this.applyMonitoringDataToLabelsForDevice(equipmentId, deviceId, data);
      }
      return;
    }

    // 같은 디바이스 재클릭
    if (activeDeviceFillVisible) {
      // 1단계(점선 + 면 채우기) → 2단계(점선만, 면 제거): 하이라이트 재생성, 태그는 유지
      const tagData = this.hotspotTagService.getTagData(equipmentId, deviceId);
      if (tagData) {
        await this.deviceHighlightService.showHighlight({
          position: tagData.anchorPosition,
          boxSize: tagData.boxSize,
          boxOffset: tagData.boxOffset,
          fillVisible: false,
        });
      }
      this.setState({ activeDeviceFillVisible: false });
    } else {
      // 2단계(점선만) → 해제: 태그/하이라이트 모두 제거
      await this.hotspotTagService.removeTag(equipmentId, deviceId);
      await this.deviceHighlightService.removeHighlight();
      // 2026.05.14 : 박은정 : 3D 오버레이 라벨 + 모니터 텍스트 라벨 모두 제거 (구성요소 카드)
      if (this.state.learningPanelCardId === 'components') {
        await this.simulationOverlayService.clear();
        this.hideAllTextLabels();
      }
      this.setState({ activeDeviceId: null, activeDeviceFillVisible: false });
    }
  }

  // 2026.02.11 : 박은정 : 학습 카드 변경 시 모니터 이미지 표시/숨김 처리 (비활성화)
  // 2026.02.11 : 박은정 : 모든 모니터는 처음부터 끝까지 항상 표시되므로 학습 카드 변경과 무관하게 유지
  // 2026.02.12 : 박은정 : 시나리오 카드를 벗어나면 플레이백 시나리오 초기화 (재생바 숨김)
  // 2026.02.12 : 박은정 : 데이터 모니터링/시뮬레이션 카드 진입 시 텍스트 라벨 표시, 이탈 시 숨김
  private handleLearningCardChange = (equipmentId: string, cardId: string | null) => {
    // 2026.07.02 : 박은정 : 시뮬레이션(scenario) 카드를 벗어나면 작동 거품 효과 제거
    // 2026.08.31 : 박은정 : 배관 흐름도 같은 시점에 제거 (거품과 동일 생명주기)
    if (cardId !== 'scenario') {
      this.bubbleEffectService.clear();
      this.flowEffectService.clear();
      this.resetFlowFillState();
    }
    // 2026.02.11 : 박은정 : 모니터 이미지는 항상 표시되므로 별도 처리 불필요

    // 2026.02.20 : 박은정 : 구성요소(components) 카드가 아닌 다른 카드로 전환 시 핫스팟 태그 제거
    if (cardId !== 'components') {
      this.hotspotTagService.removeAllTags();
      // 2026.04.23 : 박은정 : 3D 테두리 하이라이트도 함께 제거
      this.deviceHighlightService.removeHighlight();
      // 2026.04.24 : 박은정 : 카드 전환 시 면 채우기 상태도 초기화
      this.setState({ activeDeviceId: null, activeDeviceFillVisible: false });
    }

    // 2026.05.14 : 박은정 : 모니터 텍스트 라벨 (벽면 모니터-01 평면) 처리
    //   - scenario: 모든 라벨에 실측 값 표시 (기존 동작 유지)
    //   - components: 진입 시 모두 숨김 → 부품 클릭 시 해당 디바이스 라벨만 표시
    //   - 그 외: JSON 기본 더미 텍스트로 복원
    if (cardId === 'scenario') {
      this.restoreAllTextLabelsToDummy();
      const data = this.state.monitoringData[equipmentId] ?? buildMonitoringData(equipmentId);
      this.applyMonitoringDataToLabels(equipmentId, data);
    } else if (cardId === 'components') {
      this.hideAllTextLabels();
    } else {
      this.restoreAllTextLabelsToDummy();
    }

    // components/scenario 가 아닌 카드로 전환 시 3D 오버레이 라벨 제거 + 시나리오 초기화
    const overlayKeepCards = ['components', 'scenario'];
    if (cardId !== 'scenario') {
      if (!cardId || !overlayKeepCards.includes(cardId)) {
        this.simulationOverlayService.clear();
      }
      this.setState(prevState => {
        const newPlaybackScenarioId = { ...prevState.playbackScenarioId };
        delete newPlaybackScenarioId[equipmentId];
        return {
          learningPanelCardId: cardId,
          playbackScenarioId: newPlaybackScenarioId,
        };
      });
    } else {
      this.setState({ learningPanelCardId: cardId });
    }
  }

  // 2026.02.11 : 박은정 : 시나리오 선택 핸들러
  // 2026.05.14 : 박은정 : 시나리오 변경 시 이전 3D 오버레이 라벨 제거(키 집합이 다를 수 있음 → 잔류 방지)
  // 2026.05.14 : 박은정 : 시나리오 선택 시 변경되는 모든 값 필드를 미리 라벨로 배치 (재생 전에도 표시)
  // 2026.07.02 : 박은정 : 시뮬레이션 재생 중 RUN=ON 장비 위에만 거품 효과 표시 (작동 시각화)
  //   - controlValues 의 `<device>-RUN` 값이 'ON' 이면 해당 장비 작동 중으로 판정
  //     (PlaybackPanel.toMonitoringData 가 boolean 제어값을 'ON'/'OFF' 문자열로 변환)
  //   - 장비 anchor 위치(HotspotTagService)에서 거품 emitter 생성/유지, 작동 종료 시 제거
  //   - 매 tick 안전: addEmitter 는 idempotent, 미작동 emitter 만 제거
  private syncScenarioBubbles(equipmentId: string, data: MonitoringData | null) {
    // 2026.08.31 : 박은정 : 순환기 외 장비는 거품을 띄우지 않는다
    if (!BUBBLE_ENABLED_EQUIPMENT.has(equipmentId)) {
      this.bubbleEffectService.clear();
      return;
    }

    // 2026.08.31 : 박은정 : 물이 본선을 다 지나간 뒤에 거품이 일도록 대기.
    //   첫 재생에는 관이 시작점부터 차례로 채워지므로, 아직 차는 중이면 거품을 띄우지 않는다.
    //   배관 흐름 설정이 없는 장비는 종전대로 RUN=ON 즉시 거품이 인다.
    if (!this.isFlowFilled(equipmentId)) {
      this.bubbleEffectService.clear();
      this.scheduleBubbleRecheck(equipmentId, data);
      return;
    }
    this.clearFlowFillTimer();

    const controls = data?.controlValues ?? {};
    const desiredIds = new Set<string>();

    for (const [key, value] of Object.entries(controls)) {
      if (!key.endsWith('-RUN') || value !== 'ON') continue;
      const deviceId = key.substring(0, key.length - '-RUN'.length);
      // 2026.07.02 : 박은정 : 고정 위치(예제 튜닝값)가 있으면 우선 사용, 없으면 디바이스 anchor 로 폴백
      const fixedPos = BUBBLE_FIXED_POSITIONS[equipmentId]?.[deviceId];
      const position = fixedPos ?? this.hotspotTagService.getTagData(equipmentId, deviceId)?.anchorPosition;
      if (!position) continue;
      const emitterId = `${BUBBLE_ID_PREFIX}${equipmentId}-${deviceId}`;
      desiredIds.add(emitterId);
      this.bubbleEffectService.addEmitter({
        id: emitterId,
        label: deviceId,
        position,
        ...BUBBLE_EMITTER_DEFAULTS,
      });
    }

    // 더 이상 작동하지 않는(또는 이전 장비의) 거품 emitter 제거
    for (const id of this.bubbleEffectService.listEmitterIds()) {
      if (!desiredIds.has(id)) {
        this.bubbleEffectService.removeEmitter(id);
      }
    }
  }

  // 2026.08.31 : 박은정 : 지금 재생 중인 시나리오에 해당하는 흐름만 고른다.
  //   좌/우 배관은 각자 시나리오가 따로라 함께 흐르면 안 된다.
  private flowsFor(equipmentId: string): FlowConfig[] {
    const configs = EQUIPMENT_FLOWS[equipmentId];
    if (!configs) return [];
    const scenarioId = this.state.playbackScenarioId[equipmentId];
    return configs.filter(cfg => {
      const owner = FLOW_SCENARIOS[cfg.id];
      return !owner || owner === scenarioId;
    });
  }

  // 2026.08.31 : 박은정 : 이 장비의 배관 흐름이 본선 끝까지 채워졌는지.
  //   흐름 설정이 없는 장비는 기다릴 것이 없으므로 true.
  private isFlowFilled(equipmentId: string): boolean {
    const configs = this.flowsFor(equipmentId);
    if (configs.length === 0) return true;

    const filled = configs.every(cfg => this.flowEffectService.isMainFilled(cfg.id));
    // 판정이 바뀔 때만 남긴다 — 거품이 안 뜰 때 어느 단계에서 막혔는지 바로 가릴 수 있게
    if (filled !== this.lastFlowFilled) {
      this.lastFlowFilled = filled;
      const remain = Math.max(
        0, ...configs.map(cfg => this.flowEffectService.remainingFillSeconds(cfg.id)),
      );
      console.log(
        filled
          ? '[Main] 배관 충전 완료 — 거품 시작'
          : `[Main] 배관 충전 중 — 거품 대기 (남은 ${remain.toFixed(1)}초)`,
      );
    }
    return filled;
  }

  // 2026.08.31 : 박은정 : 배관이 다 차는 시각에 거품 판정을 한 번 더 돌린다.
  //   재생이 끝나 모니터링 데이터가 멈춰도 거품이 제때 시작되도록 하는 안전장치.
  private scheduleBubbleRecheck(equipmentId: string, data: MonitoringData | null) {
    const configs = this.flowsFor(equipmentId);
    if (configs.length === 0) return;

    const remain = Math.max(
      0, ...configs.map(cfg => this.flowEffectService.remainingFillSeconds(cfg.id)),
    );
    // 0 이면 흐름이 아직 만들어지지 않은 것(RUN=OFF 등) — 다음 데이터 tick 을 기다린다
    if (remain <= 0) return;

    this.clearFlowFillTimer();
    this.flowFillTimer = setTimeout(() => {
      this.flowFillTimer = null;
      this.syncScenarioBubbles(equipmentId, data);
    }, remain * 1000 + 80);
  }

  // 2026.08.31 : 박은정 : 대기 타이머와 판정 기록을 함께 초기화 (흐름을 지울 때마다 호출)
  private resetFlowFillState() {
    this.clearFlowFillTimer();
    this.lastFlowFilled = null;
  }

  private clearFlowFillTimer() {
    if (this.flowFillTimer !== null) {
      clearTimeout(this.flowFillTimer);
      this.flowFillTimer = null;
    }
  }

  // 2026.08.31 : 박은정 : 작동(RUN=ON) 중인 장비의 배관 흐름 동기화.
  //   거품과 같은 조건에서 켜고 끈다 — 장비가 도는 동안에만 관에 물/공기가 흐른다.
  //   addFlow 는 같은 id 를 다시 부르면 노드를 새로 만들지 않고 갱신하므로 매 tick 안전하다.
  private syncScenarioFlows(equipmentId: string, data: MonitoringData | null) {
    if (!EQUIPMENT_FLOWS[equipmentId]) return;

    const controls = data?.controlValues ?? {};
    const running = Object.entries(controls).some(
      ([key, value]) => key.endsWith('-RUN') && value === 'ON',
    );

    // 작동 중이 아니면 아무것도 흐르지 않는다
    const desired = running ? this.flowsFor(equipmentId) : [];
    const desiredIds = new Set(desired.map(cfg => cfg.id));

    for (const cfg of desired) {
      void this.flowEffectService.addFlow(cfg);
    }
    // 다른 시나리오의(또는 정지된) 흐름은 걷어낸다
    for (const id of this.flowEffectService.listFlowIds()) {
      if (!desiredIds.has(id)) {
        this.flowEffectService.removeFlow(id);
      }
    }
  }

  private handleScenarioSelect = async (equipmentId: string, scenarioId: string) => {
    await this.simulationOverlayService.clear();
    // 2026.07.02 : 박은정 : 시나리오 변경 시 이전 거품 효과 제거
    this.bubbleEffectService.clear();
    // 2026.08.31 : 박은정 : 배관 흐름도 함께 제거
    this.flowEffectService.clear();
    this.resetFlowFillState();
    this.setState(prevState => ({
      playbackScenarioId: {
        ...prevState.playbackScenarioId,
        [equipmentId]: scenarioId,
      },
    }));
    // 시나리오 키 일괄 사전 배치 (값은 placeholder "—") → 재생 시작 전 모든 필드 노출
    await this.simulationOverlayService.prepareScenarioLabels(equipmentId, scenarioId);
  };

  // 2026.05.13 : 박은정 : 시뮬레이션 시계열 업데이트 핸들러
  //   PlaybackPanel 이 매 tick 또는 stop 시 호출. setState updater 패턴으로 단조성 검사·리셋 처리.
  private handleChartHistoryUpdate = (updater: (prev: ChartPoint[]) => ChartPoint[]) => {
    this.setState(prev => ({ chartHistory: updater(prev.chartHistory) }));
  }

  // 2026.02.12 : 박은정 : 모니터링 데이터 업데이트 핸들러 (플레이백·monitoring 카드 공통)
  // 2026.02.12 : 박은정 : 텍스트 라벨이 표시 중(monitoring/scenario)이면 3D 텍스트도 동시 업데이트
  private handleMonitoringDataUpdate = (equipmentId: string, data: MonitoringData | null) => {
    this.setState(prevState => ({
      monitoringData: {
        ...prevState.monitoringData,
        [equipmentId]: data,
      },
    }));

    // 2026.05.14 : 박은정 : 텍스트 라벨이 표시 중(components/scenario)이면 3D 텍스트도 스트리밍 값으로 갱신
    const textVisibleCards = ['components', 'scenario'];
    const currentCardId = this.state.learningPanelCardId;
    const currentEquipmentId = this.state.learningPanelEquipmentId;
    if (
      currentEquipmentId === equipmentId &&
      currentCardId && textVisibleCards.includes(currentCardId) &&
      data
    ) {
      this.applyMonitoringDataToLabels(equipmentId, data);
    }

    // 시뮬레이션(scenario) 카드 + 시나리오 선택 상태일 때만 전체 라벨 갱신.
    // components 카드는 부품 클릭(handleDeviceClick) 시점에만 해당 장비 라벨을 노출.
    // 현재 선택된 디바이스가 있다면 데이터 변경 시 해당 디바이스 값만 갱신.
    const isScenarioActive = currentCardId === 'scenario' && this.state.playbackScenarioId[equipmentId];
    if (currentEquipmentId === equipmentId && isScenarioActive && data) {
      this.simulationOverlayService.ensureLabels(equipmentId, data);
      // 2026.08.31 : 박은정 : 배관 흐름을 먼저 만들어야 그 시점부터 관이 채워지기 시작한다
      this.syncScenarioFlows(equipmentId, data);
      // 2026.07.02 : 박은정 : 작동(RUN=ON) 중인 장비 위에 거품 효과 동기화
      //   (배관이 본선 끝까지 찬 뒤에야 실제로 거품이 인다 — syncScenarioBubbles 안에서 판정)
      this.syncScenarioBubbles(equipmentId, data);
    } else if (
      currentEquipmentId === equipmentId &&
      currentCardId === 'components' &&
      this.state.activeDeviceId &&
      data
    ) {
      this.simulationOverlayService.ensureLabelsForDevice(equipmentId, this.state.activeDeviceId, data);
    }
  }

  // 2026.02.12 : 박은정 : "실험실 둘러보기" 클릭 시 타이틀 오버레이 닫고 진입 애니메이션 시작
  private handleExplore = () => {
    this.setState({ showTitle: false, showIntroAnimation: true });
    // 2026.02.12 : 박은정 : 진입 애니메이션 재생 → 완료 후 하단 장비 탭 바 표시
    this.introAnimationService.play().then((completedNormally) => {
      // 2026.02.12 : 박은정 : 정상 완료 시 애니메이션 오버레이 닫고 푸터 표시
      // (건너뛰기로 중단된 경우 handleSkipIntro에서 처리하므로 여기서는 정상 완료만 처리)
      if (completedNormally) {
        this.setState({ showIntroAnimation: false, showFooter: true });
      }
    });
  }

  // 2026.02.12 : 박은정 : 진입 애니메이션 건너뛰기 → 마지막 Sweep + 뷰포인트 즉시 적용 후 푸터 표시
  private handleSkipIntro = async () => {
    await this.introAnimationService.skip();
    this.setState({ showIntroAnimation: false, showFooter: true });
  }

  // 2026.02.09 : 박은정 : 장비 탭 선택 → 해당 장비 Sweep으로 FLY 이동 → 완료 후 학습하기 모달 표시
  // 2026.02.10 : 박은정 : Sweep ID를 JSON 데이터 서비스에서 조회하도록 변경
  // 2026.02.11 : 박은정 : Sweep ID 디버그 로그 추가 (JSON sweepId 검증용)
  // 2026.02.11 : 박은정 : ViewpointPresetService를 통해 Sweep 이동 + 뷰포인트 프리셋(카메라 방향/줌) 적용
  // 2026.02.11 : 박은정 : 학습 진행 중(패널 열림) + 같은 장비 탭 클릭 시 Sweep 이동 + 뷰포인트만 적용 (모달 생략)
  // 2026.02.11 : 박은정 : 학습 진행 중 + 다른 장비 탭 클릭 시 패널 닫고 모달 표시 (기존 동작)
  // 2026.02.11 : 박은정 : 모니터 이미지는 항상 표시되므로 숨김 처리 제거
  private handleEquipmentSelect = async (tabId: string) => {
    // 2026.02.11 : 박은정 : 학습 진행 중 여부 및 같은 장비인지 확인
    const isLearningInProgress = this.state.showLearningPanel;
    const isSameEquipment = isLearningInProgress && this.state.learningPanelEquipmentId === tabId;

    if (isSameEquipment) {
      // 2026.02.11 : 박은정 : 같은 장비 탭 재클릭 — 패널 유지, Sweep 이동 + 뷰포인트 프리셋만 적용 (모달 표시 안 함)
      this.setState({ activeEquipment: tabId });
    } else {
      // 2026.02.11 : 박은정 : 다른 장비 탭 클릭 또는 학습 미진행 — 패널 닫고 Sweep 이동 후 모달 표시
      // 2026.02.12 : 박은정 : 장비 전환 시 플레이백 시나리오 초기화 (재생바 숨김)
      // 2026.04.24 : 박은정 : 장비 전환 시 전체 장비 라벨을 초기 더미 값으로 복원 (숨기지 않음)
      this.restoreAllTextLabelsToDummy();
      // 2026.02.20 : 박은정 : 장비 전환 시 핫스팟 태그 전체 제거
      this.hotspotTagService.removeAllTags();
      // 2026.04.23 : 박은정 : 3D 테두리 하이라이트도 함께 제거
      this.deviceHighlightService.removeHighlight();
      // 2026.05.14 : 박은정 : 시뮬레이션 3D 텍스트 박스 오버레이도 함께 제거
      this.simulationOverlayService.clear();
      // 2026.07.02 : 박은정 : 장비 전환 시 작동 거품 효과도 함께 제거
      this.bubbleEffectService.clear();
      // 2026.08.31 : 박은정 : 다른 장비의 배관 흐름이 남지 않도록 함께 제거
      this.flowEffectService.clear();
      this.resetFlowFillState();
      this.setState({ activeEquipment: tabId, showEquipmentModal: false, showLearningPanel: false, playbackScenarioId: {}, activeDeviceId: null });
    }

    const sweepId = this.equipmentDataService.getSweepId(tabId);
    // 2026.02.11 : 박은정 : 디버깅 로그 제거
    if (!sweepId || !this.sdk) return;

    try {
      // 2026.02.11 : 박은정 : ViewpointPresetService로 Sweep 이동 + 카메라 방향/줌 프리셋 적용
      const success = await this.viewpointPresetService.applyPresetByEquipmentId(tabId);
      if (!success) {
        // 2026.02.11 : 박은정 : 디버깅 로그 제거
        await this.sdk.Sweep.moveTo(sweepId, {
          transition: this.sdk.Sweep.Transition.FLY,
          transitionTime: 1500,
        });
      }

      // 2026.02.11 : 박은정 : 같은 장비 학습 중에는 모달 표시 생략 (Sweep 이동 + 뷰포인트 프리셋만 적용)
      if (!isSameEquipment) {
        // 2026.02.09 : 박은정 : Sweep 이동 완료 후 학습하기 모달 표시 (카드 상태 포함)
        this.setState({
          showEquipmentModal: true,
          modalEquipmentId: tabId,
          cardStatuses: this.progressService.getCardStatuses(tabId),
        });
      }
    } catch (error) {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
    }
  }

  // 2026.02.09 : 박은정 : 장비 학습하기 모달 닫기
  private handleCloseEquipmentModal = () => {
    this.setState({ showEquipmentModal: false, modalEquipmentId: null });
  }

  // 2026.02.09 : 박은정 : localStorage에서 학습현황을 읽어 state에 반영
  private loadLearningProgress() {
    this.setState({
      equipmentProgress: this.progressService.getAllProgress(),
    });
  }

  // 2026.02.09 : 박은정 : 학습하기 버튼 클릭 → 모달 닫고 우측 학습 패널 표시
  // 2026.02.11 : 박은정 : 디버깅 로그 제거
  private handleStartLearning = (equipmentId: string, cardId?: string) => {
    // 2026.02.09 : 박은정 : 선택된 카드를 현재 학습 카드로 설정 (없으면 마지막 학습 카드)
    const targetCard = cardId || this.progressService.getLastCardId(equipmentId);
    if (targetCard) {
      this.progressService.setCurrentCard(equipmentId, targetCard);
    }

    // 2026.02.09 : 박은정 : 모달 닫고 학습 패널 표시 + state 갱신
    this.setState({
      showEquipmentModal: false,
      showLearningPanel: true,
      learningPanelEquipmentId: equipmentId,
      learningPanelCardId: targetCard || null,
      cardStatuses: this.progressService.getCardStatuses(equipmentId),
      equipmentProgress: this.progressService.getAllProgress(),
    });
  }

  // 2026.02.09 : 박은정 : 학습 패널 닫기
  // 2026.02.11 : 박은정 : 모니터 이미지는 항상 표시되므로 숨김 처리 제거
  // 2026.02.12 : 박은정 : 학습 패널 닫을 때 플레이백 시나리오 초기화 (재생바 숨김)
  // 2026.02.12 : 박은정 : 학습 패널 닫을 때 텍스트 라벨 전체 숨김
  private handleCloseLearningPanel = () => {
    // 2026.04.24 : 박은정 : 패널 닫힘 = 모니터링/시뮬레이션 종료 → 전체 장비 라벨을 초기 더미 값으로 복원
    this.restoreAllTextLabelsToDummy();
    // 2026.02.20 : 박은정 : 학습 패널 닫을 때 핫스팟 태그 전체 제거
    this.hotspotTagService.removeAllTags();
    // 2026.04.23 : 박은정 : 3D 테두리 하이라이트도 함께 제거
    this.deviceHighlightService.removeHighlight();
    // 2026.05.14 : 박은정 : 시뮬레이션 3D 텍스트 박스 오버레이도 함께 제거
    this.simulationOverlayService.clear();

    this.setState({
      showLearningPanel: false,
      learningPanelEquipmentId: null,
      learningPanelCardId: null,
      playbackScenarioId: {},
      activeDeviceId: null,
    });
  }

  // 2026.02.09 : 박은정 : 학습 패널 내 카드 완료 처리 → localStorage 저장 + state 갱신
  private handleCardComplete = (equipmentId: string, cardId: string) => {
    this.progressService.markCardCompleted(equipmentId, cardId);
    this.setState({
      cardStatuses: this.progressService.getCardStatuses(equipmentId),
      equipmentProgress: this.progressService.getAllProgress(),
    });
  }

  // 2026.05.14 : 박은정 : 모바일 환경에서 "학습하기" 클릭 시 안내 alert (PC 안내), 학습 패널은 열지 않음
  private handleStartLearningGuarded = (equipmentId: string, cardId?: string) => {
    if (this.state.isMobile) {
      alert('학습 기능은 PC 버전에서 진행할 수 있습니다.\n현재 디바이스에서는 3D 공간 탐색만 지원됩니다.');
      this.setState({ showEquipmentModal: false, modalEquipmentId: null });
      return;
    }
    this.handleStartLearning(equipmentId, cardId);
  };

  render() {
    const { isMobile } = this.state;
    return (
      <div className='main'>
        {/* 2026.02.09 : 박은정 : 상단 헤더 바 (항상 표시) */}
        {/* 2026.02.20 : 박은정 : 타이틀 h1 태그로 변경, 버전 표시 추가, 순천제일대학교 텍스트 추가 */}
        <header className="app-header">
          <div className="app-header__left">
            <h1 className="app-header__title">디지털트윈 기반 화학 공정 실습 교육 시스템</h1>
            <span className="app-header__version">v{typeof APP_VERSION !== 'undefined' ? APP_VERSION : '1.0.1'}</span>
          </div>
          <span className="app-header__university">순천제일대학교</span>
        </header>
        {/* 2026.02.13 : 박은정 : 로딩 중 상태 표시 모달 */}
        <LoadingModal isVisible={this.state.showLoading} />
        {/* 2026.02.09 : 박은정 : 타이틀 오버레이 (Phase 2: 실험실 둘러보기) */}
        <TitleOverlay
          isVisible={this.state.showTitle}
          onExplore={this.handleExplore}
        />
        {/* 2026.02.12 : 박은정 : 진입 애니메이션 오버레이 (건너뛰기 버튼) */}
        <IntroAnimationOverlay
          isVisible={this.state.showIntroAnimation}
          onSkip={this.handleSkipIntro}
        />
        {/* 2026.02.11 : 박은정 : 플레이백 패널 — 모바일 숨김 (학습 패널 자체가 안 열리므로 불필요) */}
        {!isMobile && this.state.showLearningPanel && this.state.learningPanelCardId === 'scenario' && this.state.learningPanelEquipmentId && this.state.playbackScenarioId[this.state.learningPanelEquipmentId] && (
          <div className="playback-panel-container">
            <PlaybackPanel
              scenarioId={this.state.playbackScenarioId[this.state.learningPanelEquipmentId]}
              onStateUpdate={(data) => this.handleMonitoringDataUpdate(this.state.learningPanelEquipmentId!, data)}
              onChartHistoryUpdate={this.handleChartHistoryUpdate}
            />
          </div>
        )}
        {/* 2026.02.09 : 박은정 : 하단 장비 선택 탭 바 (PC/모바일 모두 표시 — 장비 둘러보기 가능) */}
        <EquipmentFooter
          isVisible={this.state.showFooter}
          activeTab={this.state.activeEquipment}
          onTabSelect={this.handleEquipmentSelect}
          equipmentProgress={this.state.equipmentProgress}
        />
        {/* 2026.02.09 : 박은정 : 장비 학습하기 모달 (PC/모바일 모두 표시 — 모바일에서 "학습하기" 누르면 alert 안내) */}
        <EquipmentModal
          isVisible={this.state.showEquipmentModal}
          equipmentId={this.state.modalEquipmentId}
          onClose={this.handleCloseEquipmentModal}
          onStartLearning={this.handleStartLearningGuarded}
          progress={this.state.modalEquipmentId ? (this.state.equipmentProgress[this.state.modalEquipmentId] || 0) : 0}
          cardStatuses={this.state.cardStatuses}
        />
        {/* 2026.02.11 : 박은정 : 학습 좌측 모니터링 패널 — 모바일 숨김 */}
        {!isMobile && (
          <MonitoringPanel
            isVisible={this.state.showLearningPanel}
            equipmentId={this.state.learningPanelEquipmentId}
            monitoringData={this.state.learningPanelEquipmentId ? (this.state.monitoringData[this.state.learningPanelEquipmentId] || null) : null}
            chartHistory={this.state.chartHistory}
          />
        )}
        {/* 2026.02.09 : 박은정 : 학습 우측 패널 — 모바일 숨김 */}
        {!isMobile && (
          <LearningPanel
            isVisible={this.state.showLearningPanel}
            equipmentId={this.state.learningPanelEquipmentId}
            activeCardId={this.state.learningPanelCardId || undefined}
            cardStatuses={this.state.cardStatuses}
            onClose={this.handleCloseLearningPanel}
            onCardComplete={this.handleCardComplete}
            onCardChange={this.handleLearningCardChange}
            onMonitoringDataUpdate={this.handleMonitoringDataUpdate}
            onScenarioSelect={this.handleScenarioSelect}
            onDeviceClick={this.handleDeviceClick}
            activeDeviceId={this.state.activeDeviceId}
          />
        )}
        <Frame src={this.src}></Frame>
      </div>
    );
  }

  async createCameraControl() {
    [this.sceneObject] = await this.sdk.Scene.createObjects(1);
    const cameraNode = this.sceneObject.addNode();
    const cameraPose = await this.sdk.Camera.pose.waitUntil(pose => !!pose);
    this.cameraInput = cameraNode.addComponent(cameraInputType);
    this.cameraInput.inputs.startPose = {
      position: new Vector3(
        cameraPose.position.x,
        cameraPose.position.y,
        cameraPose.position.z
      ),
      quaternion: new Quaternion().setFromEuler(
        new Euler(
          cameraPose.rotation.x * (Math.PI / 180),
          cameraPose.rotation.y * (Math.PI / 180),
          0,
          'YXZ'
        )
      ),
      projection: new Matrix4().fromArray(cameraPose.projection).transpose(),
    };
    const cameraControl = cameraNode.addComponent('mp.camera');

    const inputPath = this.sceneObject.addInputPath(cameraControl, 'camera');
    const outputPath = this.sceneObject.addOutputPath(this.cameraInput, 'camera');
    inputPath.bind(outputPath);

    cameraNode.start();
  }

}

export const objectFromQuery = (url?: string): { [key: string]: string } => {
  const regex = /[#&?]([^=]+)=([^#&?]+)/g;
  url = url || window.location.href;
  const object: { [param: string]: string } = {};
  let matches;
  while ((matches = regex.exec(url)) !== null) {
    object[matches[1]] = decodeURIComponent(matches[2]);
  }
  return object;
};
